import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity, BarChart3, Ticket, Bot, BookOpen, Inbox, KeyRound, LayoutTemplate, LogOut, Megaphone, Moon, ScrollText, Settings2, ShieldCheck, Smartphone, Sun, SunMoon, TrendingUp,
  UserCog, Users, type LucideIcon,
} from "lucide-react";
import { get, put } from "@/api/client";
import type { QueuePage } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { useRealtimeEvent, useRealtimeStatus } from "@/realtime/RealtimeProvider";
import { Avatar, Button, Switch } from "@/ui";
import logo from "@/logo-mark.svg";
import { applyTheme, readTheme, type ThemePref } from "./theme";
import "./shell.css";

const roleLabel: Record<string, string> = { super_admin: "Super admin", admin: "Admin", operator: "Operator", content_editor: "Content editor" };

type Item = { to: string; label: string; icon: LucideIcon; perm?: string; badge?: number };

export function Shell() {
  const { me, can, signOut, refresh } = useAuth();
  const qc = useQueryClient();
  const status = useRealtimeStatus();
  const [theme, setTheme] = useState<ThemePref>(readTheme);
  useEffect(() => applyTheme(theme), [theme]);

  const counts = useQuery({
    queryKey: ["queue", "counts"],
    queryFn: () => get<QueuePage>("/conversations", { limit: 1 }),
    enabled: can("conversations.read.assigned"),
    select: (p) => p.counts,
  });
  useRealtimeEvent("queue.changed", () => qc.invalidateQueries({ queryKey: ["queue"] }));

  const sections: { title: string; items: Item[] }[] = [
    { title: "Work", items: [
      { to: "/", label: "Dashboard", icon: BarChart3, perm: "analytics.read" },
      { to: "/analytics", label: "Analytics", icon: TrendingUp, perm: "analytics.read" },
      { to: "/devices", label: "Device health", icon: Smartphone, perm: "analytics.read" },
      { to: "/inbox", label: "Inbox", icon: Inbox, perm: "conversations.read.assigned", badge: counts.data?.unread },
      { to: "/users", label: "People", icon: Users, perm: "users.read.basic" },
      { to: "/broadcasts", label: "Broadcasts", icon: Megaphone, perm: "notifications.broadcast" },
      { to: "/ops", label: "Live ops", icon: Activity },
    ] },
    { title: "Product", items: [
      { to: "/content", label: "Content", icon: BookOpen, perm: "content.manage" },
      { to: "/layouts", label: "Screens", icon: LayoutTemplate, perm: "settings.manage" },
      { to: "/config", label: "Flags & settings", icon: Settings2, perm: "settings.manage" },
      { to: "/ads", label: "Ads & codes", icon: Ticket, perm: "settings.manage" },
      { to: "/ai", label: "AI", icon: Bot, perm: "ai.manage" },
    ] },
    { title: "Admin", items: [
      { to: "/staff", label: "Team", icon: UserCog, perm: "staff.manage" },
      { to: "/security", label: "Security", icon: ShieldCheck, perm: "security.read" },
      { to: "/keys", label: "Server keys", icon: KeyRound, perm: "credentials.manage" },
      { to: "/audit", label: "Audit log", icon: ScrollText, perm: "audit.read" },
    ] },
  ];

  const toggleAvailability = async (available: boolean) => {
    await put("/auth/me/availability", { availability: available ? "available" : "away" });
    refresh();
  };
  const nextTheme: Record<ThemePref, ThemePref> = { system: "light", light: "dark", dark: "system" };
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : SunMoon;

  return (
    <div className="shell">
      <nav className="sidebar" aria-label="Main">
        <div className="brand"><img src={logo} alt="" /><span>PeriFlow <small>Admin</small></span></div>
        {sections.map((s) => {
          const items = s.items.filter((i) => !i.perm || can(i.perm));
          if (!items.length) return null;
          return (
            <div key={s.title}>
              <div className="nav-section">{s.title}</div>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.to === "/"} className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
                  <i.icon size={17} aria-hidden />
                  <span className="label">{i.label}</span>
                  {!!i.badge && <span className="count" aria-label={`${i.badge} unread`}>{i.badge > 99 ? "99+" : i.badge}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
        <div className="sidebar-foot">
          <div className="conn" title={`Live updates: ${status}`}>
            <span className={`status-dot status-${status}`} aria-hidden /><span>{status === "open" ? "Live" : status === "closed" ? "Offline" : "Reconnecting…"}</span>
          </div>
          {can("conversations.reply") && (
            <label className="row" style={{ padding: "0 8px", fontSize: 12.5 }}>
              <Switch checked={me.member.availability === "available"} onChange={toggleAvailability} label="Available for new conversations" />
              <span className="label muted">Available</span>
            </label>
          )}
          <div className="me">
            <Avatar name={me.member.display_name} />
            <div className="me-text grow">
              <div className="me-name truncate">{me.member.display_name}</div>
              <div className="me-role">{roleLabel[me.member.role]}</div>
            </div>
            <Button variant="ghost" size="sm" icon={ThemeIcon} aria-label={`Theme: ${theme}`} title={`Theme: ${theme}`} onClick={() => setTheme(nextTheme[theme])} />
            <Button variant="ghost" size="sm" icon={LogOut} aria-label="Sign out" title="Sign out" onClick={signOut} />
          </div>
        </div>
      </nav>
      <main className="main"><Outlet /></main>
    </div>
  );
}

